import {
    ColumnDef,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    Row,
    useReactTable,
} from '@tanstack/react-table';
import { useCallback } from 'react';

import { GroupingColumn } from '@/cip/Optimizer/OptimizationResults/TradeExecution/TradeExecution';

import { type EnrichedOptimizerTrade } from '../../../_openapi/portfolio-optimizer';
import { DEFAULT_PAGE_SIZE } from '../../../constants';
import { Pagination } from '../../../widgets/Table/Pagination';
import { Table } from '../../../widgets/Table/Table';
import { TradeEntry } from './TradeEntry';

export const TradesTable = <T extends { trades: EnrichedOptimizerTrade[] }>({
    data,
    secondaryGrouping,
    columns,
    excludeTrades,
    viewAsset,
    viewPortfolio,
    viewTrade,
    viewAdvanced,
}: {
    data: T[];
    secondaryGrouping?: GroupingColumn;
    columns: ColumnDef<T, any>[];
    excludeTrades: (trades: EnrichedOptimizerTrade[]) => void;
    viewAsset: (sId: string) => void;
    viewPortfolio: (pId: string) => void;
    viewTrade: (trade: EnrichedOptimizerTrade) => void;
    viewAdvanced: (portfolioGuid: string, pId: string) => void;
}) => {
    const table = useReactTable({
        defaultColumn: {
            enableColumnFilter: false,
        },
        initialState: {
            pagination: {
                pageSize: DEFAULT_PAGE_SIZE,
            },
        },
        data,
        columns,
        getSubRows: (originalRow) => originalRow.trades as any,
        getRowCanExpand: (row) => row.original.trades.length > 0,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        sortDescFirst: false,
    });

    const render = useCallback(
        ({ row }: { row: Row<T> }) => (
            <TradeEntry
                secondaryGrouping={secondaryGrouping}
                row={row}
                excludeTrades={excludeTrades}
                viewAsset={viewAsset}
                viewPortfolio={viewPortfolio}
                viewTrade={viewTrade}
                viewAdvanced={viewAdvanced}
            />
        ),
        [excludeTrades, secondaryGrouping, viewAsset, viewPortfolio, viewTrade, viewAdvanced],
    );

    return (
        <>
            <Table table={table} SubComponent={render} allowXOverflow />
            <Pagination table={table} />
        </>
    );
};
